import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    User,
    Building2,
    MapPin,
    Edit,
    Save,
    X,
    CheckCircle,
    XCircle,
    FileText,
    Mail,
    Phone,
    Shield,
    Globe,
    CreditCard,
    Hash
} from 'lucide-react';
import { CLIENT_TYPE_LABELS } from '@/types/client';
import api from '@/services/api';
import { useAlert } from '@/components/AlertProvider';
import { cn } from '@/utils/cn';

interface UserProfile {
    id: number;
    email: string;
    user_type: 'client' | 'prospect' | 'admin';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    phone?: string;
    mobile_phone?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    maps_link?: string;
    siret?: string;
    is_active: boolean;
    is_email_verified: boolean;
    role?: string;
    created_at: string;
    invoice_email?: string;
    payment_terms?: string;
    payment_mode?: string;
    business_provider?: string;
    client_type?: string;
}


const UserProfile: React.FC = () => {
    const { userType, userId } = useParams<{ userType: string; userId: string }>();
    const navigate = useNavigate();
    const { showSuccess, showError } = useAlert();

    const [user, setUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState<Partial<UserProfile>>({});
    const [activeTab, setActiveTab] = useState<'profile'>('profile');

    useEffect(() => {
        loadUserProfile();
    }, [userType, userId]);

    const loadUserProfile = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/users/administration/users/${userType}/${userId}/profile`);
            setUser(response.data);
            setEditForm(response.data);
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors du chargement du profil');
        } finally {
            setLoading(false);
        }
    };


    const handleSave = async () => {
        try {
            await api.patch(`/users/administration/users/${userType}/${userId}`, editForm);
            showSuccess('Profil mis à jour avec succès');
            setEditing(false);
            loadUserProfile();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const [verifyingEmail, setVerifyingEmail] = useState(false);
    const [passwordToSet, setPasswordToSet] = useState('');
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);

    const handleVerifyEmail = async () => {
        try {
            setVerifyingEmail(true);
            await api.patch(`/users/administration/users/${userType}/${userId}/verify-email`, {
                password: passwordToSet || undefined
            });
            showSuccess('Email marqué comme vérifié');
            setShowPasswordDialog(false);
            setPasswordToSet('');
            loadUserProfile();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la vérification');
        } finally {
            setVerifyingEmail(false);
        }
    };

    const handleToggleStatus = async () => {
        try {
            const newStatus = !user?.is_active;
            await api.patch(`/users/administration/users/${userType}/${userId}`, {
                is_active: newStatus
            });
            showSuccess(newStatus ? 'Compte activé' : 'Compte désactivé');
            loadUserProfile();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la modification du statut');
        }
    };



    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <p className="text-lg text-slate-600 dark:text-slate-400">Utilisateur non trouvé</p>
                <button
                    onClick={() => navigate('/administration')}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                    Retour à l'administration
                </button>
            </div>
        );
    }

    const getUserTypeLabel = (type: string) => {
        switch (type) {
            case 'client': return 'Client';
            case 'prospect': return 'Prospect';
            case 'admin': return 'Administrateur';
            case 'apporteur': return 'Apporteur';
            default: return type;
        }
    };

    const getUserTypeColor = (type: string) => {
        switch (type) {
            case 'client': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'prospect': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
            case 'admin': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
            case 'apporteur': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
            default: return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/20 to-slate-50 dark:from-slate-950 dark:via-purple-950/10 dark:to-slate-950">
            {/* Header */}
            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/administration')}
                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                        {user.user_type === 'admin'
                                            ? `${user.first_name} ${user.last_name}`
                                            : user.company_name || `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()}
                                    </h1>
                                    <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', getUserTypeColor(user.user_type))}>
                                        {getUserTypeLabel(user.user_type)}
                                    </span>
                                    <span className={cn(
                                        "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs uppercase font-bold tracking-wide",
                                        user.is_email_verified
                                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                            : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                    )}>
                                        {user.is_email_verified ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                        {user.is_email_verified ? 'Email vérifié' : 'Non vérifié'}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{user.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {['admin', 'apporteur'].includes(user.user_type) && (
                                <button
                                    onClick={handleToggleStatus}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                                        user.is_active
                                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400"
                                            : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400"
                                    )}
                                    title={user.is_active ? "Rendre inactif" : "Rendre actif"}
                                >
                                    {user.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                    {user.is_active ? 'Désactiver' : 'Activer'}
                                </button>
                            )}
                            {!user.is_email_verified && (
                                <button
                                    onClick={() => setShowPasswordDialog(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                                >
                                    <CheckCircle className="h-4 w-4" />
                                    Vérifier l'email
                                </button>
                            )}
                            {editing ? (
                                <>
                                    <button
                                        onClick={() => setEditing(false)}
                                        className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                    >
                                        <Save className="h-4 w-4" />
                                        Enregistrer
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setEditing(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                >
                                    <Edit className="h-4 w-4" />
                                    Modifier
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-6 py-4">
                <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={cn(
                            'px-4 py-2 font-medium transition-colors border-b-2',
                            activeTab === 'profile'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                        )}
                    >
                        <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Profil
                        </div>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                {activeTab === 'profile' && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                        {/* Contact Information Section */}
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-900 dark:text-slate-100">
                                <User className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                                Informations de contact
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {user.user_type === 'admin' ? (
                                    <>
                                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                            <User className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Prénom</label>
                                                {editing ? (
                                                    <input
                                                        type="text"
                                                        value={editForm.first_name || ''}
                                                        onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                                    />
                                                ) : (
                                                    <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.first_name || '-'}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                            <User className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Nom</label>
                                                {editing ? (
                                                    <input
                                                        type="text"
                                                        value={editForm.last_name || ''}
                                                        onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                                    />
                                                ) : (
                                                    <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.last_name || '-'}</p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                            <User className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Prénom</label>
                                                {editing ? (
                                                    <input
                                                        type="text"
                                                        value={editForm.first_name || ''}
                                                        onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                                    />
                                                ) : (
                                                    <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.first_name || '-'}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                            <User className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Nom</label>
                                                {editing ? (
                                                    <input
                                                        type="text"
                                                        value={editForm.last_name || ''}
                                                        onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                                                        className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                                    />
                                                ) : (
                                                    <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.last_name || '-'}</p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                                
                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <Mail className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Email</label>
                                        {editing ? (
                                            <input
                                                type="email"
                                                value={editForm.email || ''}
                                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.email || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <Phone className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Téléphone</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={editForm.phone || editForm.mobile_phone || ''}
                                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.phone || user.mobile_phone || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                {user.user_type === 'client' && (
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg md:col-span-2">
                                        <Mail className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Email de facturation</label>
                                            {editing ? (
                                                <input
                                                    type="email"
                                                    value={editForm.invoice_email || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, invoice_email: e.target.value })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                                />
                                            ) : (
                                                <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.invoice_email || '-'}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {user.user_type !== 'admin' && (
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg md:col-span-2">
                                        <FileText className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Type de client</label>
                                            {editing ? (
                                                <select
                                                    value={editForm.client_type || ''}
                                                    onChange={(e) => setEditForm({ ...editForm, client_type: e.target.value })}
                                                    className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                                >
                                                    <option value="">Sélectionner un type</option>
                                                    {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <p className="text-sm text-slate-900 dark:text-slate-100 truncate">
                                                    {(user.client_type && CLIENT_TYPE_LABELS[user.client_type as keyof typeof CLIENT_TYPE_LABELS]) 
                                                        ? CLIENT_TYPE_LABELS[user.client_type as keyof typeof CLIENT_TYPE_LABELS] 
                                                        : user.client_type || '-'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Address Information Section */}
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-900 dark:text-slate-100">
                                <MapPin className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                                Adresse
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg md:col-span-2">
                                    <MapPin className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Adresse</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={editForm.address || ''}
                                                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100">{user.address || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <Hash className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Code postal</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={editForm.postal_code || ''}
                                                onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.postal_code || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <Building2 className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Ville</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={editForm.city || ''}
                                                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100 truncate">{user.city || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg md:col-span-2">
                                    <Globe className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Pays</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={editForm.country || ''}
                                                onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100">{user.country || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status Information Section */}
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-900 dark:text-slate-100">
                                <Shield className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                                Statut du compte
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">État</span>
                                    <span className={cn(
                                        "px-3 py-1 rounded-full text-sm font-medium",
                                        user.is_active 
                                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                    )}>
                                        {user.is_active ? 'Actif' : 'Inactif'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Email</span>
                                    <span className={cn(
                                        "px-3 py-1 rounded-full text-sm font-medium",
                                        user.is_email_verified
                                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                            : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                    )}>
                                        {user.is_email_verified ? 'Vérifié' : 'Non vérifié'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Créé le</span>
                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                                {user.user_type === 'client' && user.business_provider && (
                                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Apporteur</span>
                                        <span className="text-sm text-slate-600 dark:text-slate-400">{user.business_provider}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Legal Information Section */}
                        {['admin', 'apporteur'].includes(user.user_type) && (
                            <div className="p-6">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-900 dark:text-slate-100">
                                    <FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                                    Informations légales
                                </h2>
                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <CreditCard className="h-4 w-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">SIRET</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                value={editForm.siret || ''}
                                                onChange={(e) => setEditForm({ ...editForm, siret: e.target.value })}
                                                className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                                            />
                                        ) : (
                                            <p className="text-sm text-slate-900 dark:text-slate-100">{user.siret || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Password Dialog for Email Verification */}
            {showPasswordDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                <Shield className="h-5 w-5 text-purple-600" />
                                Vérifier l'email
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Vous pouvez définir un mot de passe pour cet utilisateur ou laisser vide pour qu'il utilise celui envoyé par email.
                            </p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Nouveau mot de passe (optionnel)
                                </label>
                                <input
                                    type="password"
                                    value={passwordToSet}
                                    onChange={(e) => setPasswordToSet(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none transition-all"
                                />
                            </div>
                        </div>
                        <div className="p-6 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowPasswordDialog(false);
                                    setPasswordToSet('');
                                }}
                                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleVerifyEmail}
                                disabled={verifyingEmail}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {verifyingEmail ? 'Vérification...' : 'Confirmer la vérification'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfile;
